import { collection, addDoc, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { generateAndUploadBillPDF } from './pdfGenerator';
import { getBookingOfferBreakdown } from './bookingOfferBreakdown';

/**
 * Generate a unique bill number
 * Format: BILL-YYYYMMDD-XXXXX
 */
export const generateBillNumber = async (salonId) => {
  try {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0].replace(/-/g, ''); // YYYYMMDD
    
    // Query bills created today for this salon
    const todayStart = new Date(today.setHours(0, 0, 0, 0)).toISOString();
    const todayEnd = new Date(today.setHours(23, 59, 59, 999)).toISOString();
    
    const billsQuery = query(
      collection(db, 'bills'),
      where('salonId', '==', salonId),
      where('createdAt', '>=', todayStart),
      where('createdAt', '<=', todayEnd)
    );
    
    const billsSnapshot = await getDocs(billsQuery);
    const billCount = billsSnapshot.size + 1;
    
    // Pad with zeros to make it 5 digits
    const sequenceNumber = String(billCount).padStart(5, '0');
    
    return `BILL-${dateStr}-${sequenceNumber}`;
  } catch (error) {
    // Fallback to timestamp-based bill number
    return `BILL-${Date.now()}`;
  }
};

/**
 * Calculate bill totals
 */
export const calculateBillTotals = (services, discounts = []) => {
  // Calculate subtotal from services
  const subtotal = services.reduce((sum, service) => sum + (service.price || 0), 0);
  
  // Calculate total discount
  const totalDiscount = discounts.reduce((sum, discount) => sum + (discount.amount || 0), 0);
  
  // Calculate grand total
  const grandTotal = Math.max(0, subtotal - totalDiscount);
  
  return {
    subtotal,
    totalDiscount,
    grandTotal
  };
};

/**
 * Create bill data structure from booking
 */
export const createBillFromBooking = async (booking, salonData, paymentMode) => {
  try {
    // Generate bill number
    const billNumber = await generateBillNumber(salonData.salonId);
    
    // Prepare services array
    const services = [];
    
    // Check if it's a combo service with multiple services
    if (booking.isCombo && booking.comboServices && booking.comboServices.length > 0) {
      // Add each service from combo
      booking.comboServices.forEach((service) => {
        services.push({
          name: service.name || service,
          price: service.price || 0
        });
      });
    } else {
      // Single service
      services.push({
        name: booking.serviceName,
        price: booking.servicePrice || booking.totalPrice || 0
      });
    }
    
    const listTotal = services.reduce((sum, s) => sum + (s.price || 0), 0);
    const finalPrice = booking.totalPrice || booking.servicePrice || 0;

    const br = getBookingOfferBreakdown({
      ...booking,
      originalPrice: listTotal,
      totalPrice: finalPrice
    });

    const discounts = [];
    if (br.salonOfferAmount > 0) {
      discounts.push({ description: 'Salon offer', amount: br.salonOfferAmount });
    }
    if (br.ulloorOfferAmount > 0) {
      discounts.push({ description: 'Ulloor Styles offer', amount: br.ulloorOfferAmount });
    }
    if (br.otherDiscountAmount > 0) {
      discounts.push({ description: 'Other offers', amount: br.otherDiscountAmount });
    }

    if (discounts.length === 0 && booking.offerApplied) {
      const discountAmount = listTotal - finalPrice;
      if (discountAmount > 0) {
        discounts.push({ description: 'Offer applied', amount: discountAmount });
      }
    }

    // GST: salon had it turned on at booking time. Older bookings won't have these fields
    // — treat gstEnabled as falsy/undefined, same as no GST applied.
    const gstEnabled = !!booking.gstEnabled;
    const gstPercentage = gstEnabled ? (Number(booking.gstPercentage) || 0) : 0;
    const gstAmount = gstEnabled ? (Number(booking.gstAmount) || 0) : 0;

    // Calculate totals (pre-tax), then layer GST on top for the final payable amount
    const { subtotal, totalDiscount, grandTotal: preTaxTotal } = calculateBillTotals(services, discounts);
    const grandTotal = gstEnabled ? preTaxTotal + gstAmount : preTaxTotal;

    // Create bill data
    const billData = {
      billNumber,
      bookingId: booking.id,
      bookingReference: booking.bookingReference,
      salonId: salonData.salonId,
      salonName: salonData.profile?.shopName || 'Ulloor Styles',
      salonPhone: salonData.phoneNumber || salonData.profile?.phoneNumber || '',
      salonAddress: salonData.profile?.address || '',
      customerName: booking.customerName,
      customerPhone: booking.customerPhone || '',
      services,
      discounts,
      subtotal,
      totalDiscount,
      gstEnabled,
      gstPercentage,
      gstAmount,
      grandTotal,
      paymentMode,
      billStatus: 'PAID',
      isWalkIn: booking.isWalkIn || false,
      createdAt: new Date().toISOString(),
      billDate: new Date().toISOString()
    };
    
    // Save bill to Firestore
    const billRef = await addDoc(collection(db, 'bills'), billData);
    
    const billWithId = {
      id: billRef.id,
      ...billData
    };
    
    // Generate and upload PDF immediately after bill creation
    try {
      const { pdfUrl, pdfFileName } = await generateAndUploadBillPDF(billWithId);
      
      // Update bill document with PDF URL
      await updateDoc(doc(db, 'bills', billRef.id), {
        pdfUrl,
        pdfFileName,
        pdfGeneratedAt: new Date().toISOString()
      });
      
      
      // Return bill with PDF URL
      return {
        ...billWithId,
        pdfUrl,
        pdfFileName
      };
    } catch (pdfError) {
      // Return bill without PDF - it can be generated later
      return billWithId;
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Get bill by booking ID
 */
export const getBillByBookingId = async (bookingId, salonId) => {
  const billsQuery = query(
    collection(db, 'bills'),
    where('bookingId', '==', bookingId),
    where('salonId', '==', salonId)
  );

  const billsSnapshot = await getDocs(billsQuery);

  if (!billsSnapshot.empty) {
    const billDoc = billsSnapshot.docs[0];
    return {
      id: billDoc.id,
      ...billDoc.data()
    };
  }

  return null;
};

/**
 * Format bill data for WhatsApp message
 */
export const formatBillForWhatsApp = (bill) => {
  let message = `*${bill.salonName}*\n`;
  if (bill.salonPhone) {
    message += `📞 ${bill.salonPhone}\n`;
  }
  message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  message += `*BILL: ${bill.billNumber}*\n`;
  message += `Date: ${new Date(bill.billDate).toLocaleString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })}\n`;
  message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  message += `*Customer Details*\n`;
  message += `Name: ${bill.customerName}\n`;
  if (bill.customerPhone) {
    message += `Phone: ${bill.customerPhone}\n`;
  }
  message += `\n━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  message += `*Services*\n`;
  bill.services.forEach((service, index) => {
    message += `${index + 1}. ${service.name}\n`;
    message += `   ₹${service.price.toFixed(2)}\n`;
  });
  message += `\n━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  message += `*Bill Summary*\n`;
  message += `Subtotal: ₹${bill.subtotal.toFixed(2)}\n`;
  
  if (bill.discounts && bill.discounts.length > 0) {
    bill.discounts.forEach((discount) => {
      message += `${discount.description}: -₹${discount.amount.toFixed(2)}\n`;
    });
  }

  if (bill.gstEnabled) {
    message += `GST (${bill.gstPercentage}%): ₹${Number(bill.gstAmount || 0).toFixed(2)}\n`;
  }

  message += `\n*Grand Total: ₹${bill.grandTotal.toFixed(2)}*\n\n`;
  
  message += `━━━━━━━━━━━━━━━━━━━━\n`;
  message += `Payment Mode: ${bill.paymentMode.toUpperCase()}\n`;
  message += `Status: ${bill.billStatus}\n`;
  message += `━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  message += `Thank you for visiting ${bill.salonName}! 🙏`;
  
  return message;
};

